import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { ordersApi, type UpdateOrderStatusDto, type RefundOrderDto } from '../api/orders.api';
import type { Order } from '../types';

const PAGINATED_STATUSES = ['DELIVERED', 'CANCELED'];
const PAGE_SIZE = 50;
export const ADMIN_ORDERS_PAGE_SIZE = 25;

export type AdminOrdersListFilters = {
  statuses?: string[];
  cities?: string[];
  sellerIds?: number[];
  dateFrom?: string;
  dateTo?: string;
  delivery?: Array<'PICKUP' | 'DOSTAVISTA' | 'SELLER'>;
};

export const useAdminOrdersPage = (
  filters: AdminOrdersListFilters,
  searchQuery: string,
  page: number,
) => {
  const search = searchQuery.trim();
  const safePage = page > 0 ? page : 1;

  return useQuery<{ orders: Order[]; total: number }>({
    queryKey: ['orders', 'admin', 'list', filters, search, safePage],
    queryFn: () =>
      ordersApi.adminList({
        ...filters,
        search,
        limit: ADMIN_ORDERS_PAGE_SIZE,
        offset: (safePage - 1) * ADMIN_ORDERS_PAGE_SIZE,
      }),
  });
};

export const useOrders = () => {
  return useQuery<Order[]>({
    queryKey: ['orders'],
    queryFn: () => ordersApi.getAll(),
  });
};

export const useOrder = (id: number) => {
  return useQuery<Order>({
    queryKey: ['orders', id],
    queryFn: () => ordersApi.getById(id),
    enabled: !!id,
  });
};

export const useOrderStatusHistory = (orderId: number, enabled: boolean) => {
  return useQuery({
    queryKey: ['orders', orderId, 'status-history'],
    queryFn: () => ordersApi.getStatusHistory(orderId),
    enabled: !!orderId && enabled,
  });
};

export const useOrdersByPickupPoint = (
  pickupPointId: number,
  status?: string | null,
  searchQuery?: string,
) => {
  const isPaginated = !!status && PAGINATED_STATUSES.includes(status);
  const search = searchQuery != null ? searchQuery.trim() : '';

  const infiniteQuery = useInfiniteQuery({
    queryKey: ['orders', 'pickup-point', pickupPointId, status ?? '', 'paginated', search],
    queryFn: async ({ pageParam }) => {
      const res = await ordersApi.getByPickupPointIdPage(pickupPointId, status!, {
        limit: PAGE_SIZE,
        offset: pageParam,
        ...(search !== '' && { search }),
      });
      return res;
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.orders.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    initialPageParam: 0,
    enabled: !!pickupPointId && isPaginated,
  });

  const regularQuery = useQuery<Order[]>({
    queryKey: ['orders', 'pickup-point', pickupPointId, status ?? ''],
    queryFn: () => ordersApi.getByPickupPointId(pickupPointId, status),
    enabled: !!pickupPointId && !isPaginated,
  });

  if (isPaginated) {
    const data = infiniteQuery.data?.pages.flatMap((p) => p.orders) ?? [];
    return {
      data,
      isLoading: infiniteQuery.isLoading,
      error: infiniteQuery.error,
      refetch: infiniteQuery.refetch,
      isFetching: infiniteQuery.isFetching,
      fetchNextPage: infiniteQuery.fetchNextPage,
      hasNextPage: infiniteQuery.hasNextPage,
      isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    };
  }

  return {
    data: regularQuery.data ?? [],
    isLoading: regularQuery.isLoading,
    error: regularQuery.error,
    refetch: regularQuery.refetch,
    isFetching: regularQuery.isFetching,
    fetchNextPage: undefined,
    hasNextPage: false,
    isFetchingNextPage: false,
  };
};

export const useUpdateOrderStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, dto }: { orderId: number; dto: UpdateOrderStatusDto }) =>
      ordersApi.updateStatus(orderId, dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['orders', variables.orderId] });
    },
  });
};

export const useUpdateOrderReady = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, files, comment }: { orderId: number; files: File[]; comment?: string }) =>
      ordersApi.updateReady(orderId, files, comment),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['orders', variables.orderId] });
    },
  });
};

export const useRefundOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, dto }: { orderId: number; dto?: RefundOrderDto }) =>
      ordersApi.refund(orderId, dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['orders', variables.orderId] });
    },
  });
};
